import { Client } from "creso-core/types";
import { useRequestNriKyc } from "creso-core/hooks";
import { isNRI } from "creso-core/utils";
import { format, parseISO } from "date-fns";
import { CheckCircle2, Info, Loader2Icon, XCircle } from "lucide-react";
import { Button } from "web-design-system";
import { handleError } from "@/utils/error";

type KycSubject = Pick<
  Client,
  "id" | "name" | "kyc_status" | "kyc_status_remark" | "tax_status"
>;

export function KycStatusBadge({
  kyc_status,
  remark,
}: {
  kyc_status?: string;
  remark?: string | null;
}) {
  if (kyc_status === "S") {
    if (remark === "KYC REGISTERED") {
      return (
        <div className="flex items-center gap-1.5 shrink-0 rounded-lg bg-muted px-3 py-2">
          <Info className="size-4 text-caution" />
          <span className="text-sm font-semibold">Registered</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 shrink-0 rounded-lg bg-muted px-3 py-2">
        <CheckCircle2 className="size-4 text-constructive" />
        <span className="text-sm font-semibold">KYC Verified</span>
      </div>
    );
  }
  if (kyc_status === "F") {
    return (
      <div className="flex items-center gap-1.5 shrink-0 rounded-lg bg-muted px-3 py-2">
        <XCircle className="size-4 text-destructive" />
        <span className="text-sm font-semibold">Not Verified</span>
      </div>
    );
  }
  return null;
}

/**
 * `onCheckStatus` re-runs the KYC check, e.g. after an NRI KYC request is
 * done by Creso support.
 */
export function KycDescription({
  client,
  cachedName,
  actionHint = "once you click on create account",
  onCheckStatus,
  isCheckingStatus,
}: {
  client: KycSubject | null;
  cachedName?: string | null;
  actionHint?: string;
  onCheckStatus?: () => void;
  isCheckingStatus?: boolean;
}) {
  const nriKycRequest = useRequestNriKyc(client?.id ?? 0);
  const requestedAt = nriKycRequest.data?.requested_at;

  if (!client) return null;
  const firstName = client.name?.split(" ")[0] ?? "";

  if (client.kyc_status === "S") {
    if (cachedName) {
      return (
        <div className="rounded-lg p-3 border border-border text-xs text-muted-foreground">
          Updated &ldquo;{cachedName}&rdquo; with name found on PAN. Check PAN
          no. if there&apos;s a mismatch.
        </div>
      );
    }
    if (client.kyc_status_remark === "KYC REGISTERED") {
      return (
        <div className="rounded-lg p-3 border border-border text-xs">
          <span className="font-semibold">KYC Registered.</span>{" "}
          <span className="text-muted-foreground">
            Can transact with existing folios of AMCs for now. {firstName} can
            validate KYC or apply for re-KYC once they are active.
          </span>
        </div>
      );
    }
    return null;
  }

  if (client.kyc_status === "F" && isNRI(client as Client)) {
    const isBusy = nriKycRequest.isPending || !!isCheckingStatus;
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg p-4 bg-tag-orange border border-tag-yellow-foreground">
        <span className="text-xs text-tag-yellow-foreground">
          {requestedAt
            ? `NRI KYC requested on ${format(parseISO(requestedAt), "dd MMM yyyy")}. Creso support will complete it.`
            : "No KYC found. NRI KYC is done by Creso support."}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isBusy}
          className="border-tag-yellow-foreground text-tag-yellow-foreground hover:text-tag-yellow-foreground focus-visible:text-tag-yellow-foreground"
          onClick={() => {
            if (requestedAt) {
              onCheckStatus?.();
            } else {
              // Safe to repeat: a second request returns the first one's date
              nriKycRequest
                .mutateAsync()
                .catch((error) =>
                  handleError(error, "Failed to raise NRI KYC request")
                );
            }
          }}
        >
          {isBusy && <Loader2Icon className="size-4 animate-spin" />}
          {requestedAt ? "Check status" : "Raise NRI KYC request"}
        </Button>
      </div>
    );
  }

  if (client.kyc_status === "F") {
    return (
      <div className="rounded-lg p-3 border border-border text-xs">
        <span className="font-semibold">KYC Required.</span>{" "}
        <span className="text-muted-foreground">
          {firstName} will receive KYC link via SMS {actionHint}. They can also
          complete KYC from their app.
        </span>
      </div>
    );
  }

  return null;
}
